import {useCallback} from 'react';
import {useLatest} from 'ahooks';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {DragDropUploadState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {handleDragDropFilesUpload, retryPendingUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import type {ReferenceImageUploadState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-reference-image-upload.library';
import {handleReferenceImageFilesUpload, retryReferenceImageUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-reference-image-upload.library';
import {MAX_ATTACHMENTS} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {isPendingImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';

export interface UseAiPromptBoxDragDropUploader {
  handleFiles: (files: File[]) => void;
  handleReferenceImageFiles: (files: File[]) => void;
  retryUpload: (localId: string) => void;
  showInvalidFileTypeError: VoidFunction;
}

export const useAiPromptBoxDragDropUploader = (id: string): UseAiPromptBoxDragDropUploader => {
  const {state, actions} = useAiPromptBox(id);
  const attachmentsRef = useLatest(state.attachments);

  const showInvalidFileTypeError = useCallback((): void => {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_ai_chat_unsupported_file_type'),
      showCloseIcon: true,
      interval: 7000,
    });
  }, []);

  const buildUploadState = useCallback(
    (): DragDropUploadState => ({
      promptBoxId: id,
      getAttachmentCount: (): number => attachmentsRef.current?.items.length ?? 0,
      setActive: actions.setActiveIfNotProcessing,
      removeImageAttachments: actions.removeImageAttachments,
      addPendingImageAttachments: actions.addPendingImageAttachments,
      resolvePendingAttachment: actions.resolvePendingAttachment,
      markPendingAttachmentFailed: actions.markPendingAttachmentFailed,
      markPendingAttachmentUploading: actions.markPendingAttachmentUploading,
    }),
    [id, attachmentsRef, actions]
  );

  const buildReferenceImageUploadState = useCallback(
    (): ReferenceImageUploadState => ({
      promptBoxId: id,
      getAttachmentCount: (): number => attachmentsRef.current?.items.length ?? 0,
      getMaxAttachments: (): number => attachmentsRef.current?.maxAttachments ?? MAX_ATTACHMENTS,
      setActive: actions.setActiveIfNotProcessing,
      addPendingImageAttachments: actions.addPendingImageAttachments,
      resolvePendingAttachment: actions.resolvePendingAttachment,
      markPendingAttachmentFailed: actions.markPendingAttachmentFailed,
      markPendingAttachmentUploading: actions.markPendingAttachmentUploading,
    }),
    [id, attachmentsRef, actions]
  );

  const handleFiles = useCallback(
    (files: File[]): void => {
      handleDragDropFilesUpload(files, buildUploadState(), showInvalidFileTypeError);
    },
    [buildUploadState, showInvalidFileTypeError]
  );

  const handleReferenceImageFiles = useCallback(
    (files: File[]): void => {
      handleReferenceImageFilesUpload(files, buildReferenceImageUploadState());
    },
    [buildReferenceImageUploadState]
  );

  const retryUpload = useCallback(
    (localId: string): void => {
      const pendingAttachment = (attachmentsRef.current?.items ?? []).find((item) => isPendingImageAttachment(item) && item.localId === localId);
      if (pendingAttachment?.type === PromptAttachmentType.REFERENCE_IMAGE) {
        retryReferenceImageUpload(localId, buildReferenceImageUploadState());
        return;
      }
      retryPendingUpload(localId, buildUploadState());
    },
    [attachmentsRef, buildReferenceImageUploadState, buildUploadState]
  );

  return {handleFiles, handleReferenceImageFiles, retryUpload, showInvalidFileTypeError};
};
